import React from "react";
import type { Event, Entity, Relationship, Source, Assessment } from "@nexus/contracts";
import { LAYER_METADATA, REL_TYPE_LABELS } from "../types";
import {
  X,
  Calendar,
  AlertTriangle,
  MapPin,
  ExternalLink,
  Activity,
  FileCheck2,
} from "lucide-react";

interface EventDetailModalProps {
  event: Event | null;
  sources?: Source[];
  assessments?: Assessment[];
  relationships: Relationship[];
  entities: Entity[];
  onClose: () => void;
  onSelectEntity: (entityId: string) => void;
}

export const EventDetailModal: React.FC<EventDetailModalProps> = ({
  event,
  sources = [],
  assessments = [],
  relationships,
  entities,
  onClose,
  onSelectEntity,
}) => {
  if (!event) return null;

  const entityMap = new Map<string, Entity>();
  for (const e of entities) {
    entityMap.set(e.id, e);
  }

  const actor = entityMap.get(event.actorId);
  const targets = event.targetIds.map((id) => entityMap.get(id)).filter(Boolean) as Entity[];
  const attributedActor = event.attribution
    ? entityMap.get(event.attribution.attributedActorId)
    : null;

  // Filter related relationships between actor and targets
  const relatedRels = relationships.filter((rel) => {
    const isActorInvolved = rel.fromId === event.actorId || rel.toId === event.actorId;
    const isTargetInvolved =
      event.targetIds.includes(rel.fromId) || event.targetIds.includes(rel.toId);
    return isActorInvolved || isTargetInvolved;
  });

  const formattedDate = new Date(event.occurredAt).toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "6px",
                background: "rgba(244, 63, 94, 0.15)",
                border: "1px solid var(--accent-rose)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Activity size={18} color="var(--accent-rose)" />
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <h2 style={{ fontSize: "16px", fontWeight: 700, color: "#ffffff" }}>
                  {event.titleKo}
                </h2>
                <div className="severity-box font-mono">
                  <span style={{ fontSize: "11px", color: "#f43f5e", fontWeight: 700 }}>
                    SEV-{event.severity}
                  </span>
                  <span className="editor-tag">편집자 판단값</span>
                </div>
              </div>
              <div style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "2px" }}>
                {event.titleEn} · ID: <span className="font-mono">{event.id}</span>
              </div>
            </div>
          </div>

          <button className="btn-icon" onClick={onClose} title="닫기">
            <X size={16} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="modal-body">
          {/* Disputed Attribution Alert Banner */}
          {event.attribution?.disputed && (
            <div className="disputed-banner">
              <AlertTriangle size={18} color="#ef4444" />
              <div>
                <div style={{ fontSize: "13px", fontWeight: 700 }}>귀속 이견 있음 (DISPUTED ATTRIBUTION)</div>
                <div style={{ fontSize: "11px", color: "#fee2e2", fontWeight: 400, marginTop: "2px" }}>
                  이 사건의 행위 주체에 대해 공인 기관 간 분석 결과가 일치하지 않거나 이견이 제기되었습니다.
                </div>
              </div>
            </div>
          )}

          {/* Quick Meta Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: "10px",
              marginBottom: "16px",
            }}
          >
            <div style={{ background: "rgba(19, 29, 51, 0.5)", border: "1px solid var(--border-subtle)", borderRadius: "6px", padding: "10px" }}>
              <div style={{ fontSize: "10px", color: "var(--text-muted)", marginBottom: "4px", display: "flex", alignItems: "center", gap: "4px" }}>
                <Calendar size={11} /> 발생 일시 (정밀도: {event.precision})
              </div>
              <div className="font-mono" style={{ fontSize: "12px", fontWeight: 600, color: "#ffffff" }}>
                {formattedDate}
              </div>
            </div>

            <div style={{ background: "rgba(19, 29, 51, 0.5)", border: "1px solid var(--border-subtle)", borderRadius: "6px", padding: "10px" }}>
              <div style={{ fontSize: "10px", color: "var(--text-muted)", marginBottom: "4px" }}>
                사건 유형 (Event Type)
              </div>
              <div className="font-mono" style={{ fontSize: "12px", fontWeight: 600, color: "var(--accent-cyan)" }}>
                {event.eventType}
              </div>
            </div>

            <div style={{ background: "rgba(19, 29, 51, 0.5)", border: "1px solid var(--border-subtle)", borderRadius: "6px", padding: "10px" }}>
              <div style={{ fontSize: "10px", color: "var(--text-muted)", marginBottom: "4px" }}>
                관련 레이어 ({event.layers.length})
              </div>
              <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                {event.layers.map((l) => {
                  const meta = LAYER_METADATA[l as keyof typeof LAYER_METADATA];
                  return (
                    <span
                      key={l}
                      style={{
                        fontSize: "10px",
                        fontWeight: 600,
                        color: meta?.color || "#ffffff",
                        background: meta ? `${meta.color}22` : "rgba(255,255,255,0.1)",
                        padding: "1px 5px",
                        borderRadius: "3px",
                      }}
                    >
                      {l}
                    </span>
                  );
                })}
              </div>
            </div>

            <div style={{ background: "rgba(19, 29, 51, 0.5)", border: "1px solid var(--border-subtle)", borderRadius: "6px", padding: "10px" }}>
              <div style={{ fontSize: "10px", color: "var(--text-muted)", marginBottom: "4px", display: "flex", alignItems: "center", gap: "4px" }}>
                <MapPin size={11} /> 발생 좌표 (Geo)
              </div>
              <div className="font-mono" style={{ fontSize: "11px", color: "var(--text-secondary)" }}>
                {event.geo ? `${event.geo.lat.toFixed(2)}°, ${event.geo.lon.toFixed(2)}°` : "행위국가 기본 좌표"}
              </div>
            </div>
          </div>

          {/* Actor & Targets Section */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "16px",
              marginBottom: "16px",
            }}
          >
            {/* Actor Card */}
            <div style={{ background: "rgba(15, 23, 42, 0.6)", border: "1px solid var(--border-subtle)", borderRadius: "6px", padding: "12px" }}>
              <div style={{ fontSize: "10px", fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", marginBottom: "6px" }}>
                행위 주체 (ACTOR)
              </div>
              {actor ? (
                <div
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
                  onClick={() => onSelectEntity(actor.id)}
                >
                  <div>
                    <div style={{ fontSize: "14px", fontWeight: 700, color: "#ffffff" }}>
                      {actor.nameKo} ({actor.nameEn})
                    </div>
                    <div style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "2px" }}>
                      유형: {actor.type.toUpperCase()} · ID: {actor.id}
                    </div>
                  </div>
                  <span className="btn-secondary" style={{ fontSize: "10px" }}>프로필 열기</span>
                </div>
              ) : (
                <div className="font-mono" style={{ color: "var(--text-muted)" }}>{event.actorId}</div>
              )}
            </div>

            {/* Target Entities Card */}
            <div style={{ background: "rgba(15, 23, 42, 0.6)", border: "1px solid var(--border-subtle)", borderRadius: "6px", padding: "12px" }}>
              <div style={{ fontSize: "10px", fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", marginBottom: "6px" }}>
                대상 실체 (TARGETS, {targets.length})
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                {targets.map((tgt) => (
                  <div
                    key={tgt.id}
                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
                    onClick={() => onSelectEntity(tgt.id)}
                  >
                    <span style={{ fontSize: "13px", fontWeight: 600, color: "#ffffff" }}>
                      {tgt.nameKo} <span style={{ fontSize: "11px", color: "var(--text-muted)" }}>({tgt.nameEn})</span>
                    </span>
                    <span className="btn-secondary" style={{ fontSize: "10px" }}>보기</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Attribution Breakdown (if cyber/military operation) */}
          {event.attribution && (
            <div
              style={{
                background: "rgba(30, 27, 75, 0.4)",
                border: "1px solid rgba(168, 85, 247, 0.4)",
                borderRadius: "6px",
                padding: "12px",
                marginBottom: "16px",
              }}
            >
              <div style={{ fontSize: "11px", fontWeight: 700, color: "#d8b4fe", textTransform: "uppercase", marginBottom: "6px" }}>
                위협 행위자 귀속 분석 (ATTRIBUTION DOSSIER)
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px", fontSize: "12px" }}>
                <div>
                  <span style={{ color: "var(--text-muted)", fontSize: "10px" }}>지목된 배후 실체:</span>
                  <div style={{ fontWeight: 600, color: "#ffffff", marginTop: "2px" }}>
                    {attributedActor ? `${attributedActor.nameKo} (${attributedActor.nameEn})` : event.attribution.attributedActorId}
                  </div>
                </div>
                <div>
                  <span style={{ color: "var(--text-muted)", fontSize: "10px" }}>귀속 판정 주체:</span>
                  <div className="font-mono" style={{ color: "var(--accent-cyan)", marginTop: "2px" }}>
                    {event.attribution.attributedBy.toUpperCase()}
                  </div>
                </div>
                <div>
                  <span style={{ color: "var(--text-muted)", fontSize: "10px" }}>귀속 신뢰도 수준:</span>
                  <div className="font-mono" style={{ color: event.attribution.confidence === "high" ? "#10b981" : "#f59e0b", fontWeight: 700, marginTop: "2px" }}>
                    {event.attribution.confidence.toUpperCase()} CONFIDENCE
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Event Summary */}
          <div style={{ background: "rgba(15, 23, 42, 0.8)", border: "1px solid var(--border-subtle)", borderRadius: "6px", padding: "14px", marginBottom: "16px" }}>
            <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", marginBottom: "6px" }}>
              사건 개요 (SUMMARY)
            </div>
            <p style={{ fontSize: "13px", lineHeight: 1.6, color: "#f1f5f9" }}>
              {event.summaryKo}
            </p>
          </div>

          {/* Assessments (if any) */}
          {assessments.length > 0 && (
            <div style={{ marginBottom: "16px" }}>
              <div className="section-title">
                <span>전문가 / 모델 평가 (ASSESSMENTS, {assessments.length})</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                {assessments.map((a) => (
                  <div
                    key={a.id}
                    style={{
                      background: "rgba(19, 29, 51, 0.5)",
                      border: "1px solid var(--border-subtle)",
                      borderRadius: "6px",
                      padding: "8px 12px",
                      fontSize: "12px",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
                      <span style={{ color: "var(--accent-cyan)", fontWeight: 600, display: "flex", alignItems: "center", gap: "4px" }}>
                        <FileCheck2 size={12} /> {a.author === "model" ? `AI 분석 (${a.modelName || "모델"})` : "편집위원 분석"}
                      </span>
                      <span className="font-mono" style={{ fontSize: "10px", color: "var(--text-muted)" }}>
                        신뢰도: {a.confidence}
                      </span>
                    </div>
                    <p style={{ color: "#e2e8f0" }}>{a.claimKo}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sources & Citations */}
          <div style={{ marginBottom: "16px" }}>
            <div className="section-title">
              <span>출처 및 인용 검증 (SOURCES, {event.sourceIds.length})</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {sources.length > 0 ? (
                sources.map((src) => (
                  <div
                    key={src.id}
                    style={{
                      background: "rgba(19, 29, 51, 0.4)",
                      border: "1px solid var(--border-subtle)",
                      borderRadius: "6px",
                      padding: "10px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <div style={{ fontSize: "12px", fontWeight: 600, color: "#ffffff" }}>
                        {src.publisher}
                      </div>
                      <div style={{ fontSize: "11px", color: "var(--text-secondary)", marginTop: "2px" }}>
                        {src.title}
                      </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      {src.type === "placeholder" ? (
                        <span style={{ fontSize: "10px", color: "var(--accent-rose)", background: "rgba(244, 63, 94, 0.15)", padding: "3px 6px", borderRadius: "3px" }}>
                          Placeholder source — synthetic V0
                        </span>
                      ) : src.url ? (
                        <a
                          href={src.url}
                          target="_blank"
                          rel="noreferrer"
                          className="btn-secondary"
                          style={{ display: "flex", alignItems: "center", gap: "4px", color: "var(--accent-cyan)" }}
                        >
                          문서 링크 <ExternalLink size={11} />
                        </a>
                      ) : null}
                      <span className="font-mono" style={{ fontSize: "10px", color: "var(--text-muted)" }}>
                        등급: {src.reliability}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>
                  연계 출처 ID: {event.sourceIds.join(", ")}
                </div>
              )}
            </div>
          </div>

          {/* Related Relationships */}
          {relatedRels.length > 0 && (
            <div>
              <div className="section-title">
                <span>관련 역학 관계망 (RELATED RELATIONSHIPS, {relatedRels.length})</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                {relatedRels.map((rel) => {
                  const from = entityMap.get(rel.fromId);
                  const to = entityMap.get(rel.toId);
                  const relMeta = REL_TYPE_LABELS[rel.relType] || { labelKo: rel.relType, color: "#06b6d4" };

                  return (
                    <div
                      key={rel.id}
                      style={{
                        background: "rgba(15, 23, 42, 0.6)",
                        border: "1px solid var(--border-subtle)",
                        borderRadius: "6px",
                        padding: "8px 10px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div style={{ fontSize: "11px" }}>
                        <strong>{from?.nameKo || rel.fromId}</strong> ➔ <strong>{to?.nameKo || rel.toId}</strong>
                      </div>
                      <span
                        style={{
                          fontSize: "10px",
                          fontWeight: 600,
                          color: relMeta.color,
                          background: `${relMeta.color}15`,
                          padding: "2px 6px",
                          borderRadius: "4px",
                        }}
                      >
                        {relMeta.labelKo} (강도 {rel.strength})
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
